import { AgendaModule } from "@/components/modules/agenda/AgendaPublic";
import { CompetitionModule } from "@/components/modules/competition/CompetitionPublic";
import { DonationModule } from "@/components/modules/donation/DonationPublic";
import { HeroModule } from "@/components/modules/hero/HeroPublic";
import { JuniorModule } from "@/components/modules/junior/JuniorPublic";
import { LocationModule } from "@/components/modules/location/LocationPublic";
import { PotluckModule } from "@/components/modules/potluck/PotluckPublic";
import { RsvpModule } from "@/components/modules/rsvp/RsvpPublic";
import type { Event, ModuleConfigMap, ModuleId } from "@/lib/event-schema";
import type { ComponentType } from "react";

export type PublicModule<K extends ModuleId> = ComponentType<{
  config: ModuleConfigMap[K];
  event: Event;
}>;

type RegistryEntry<K extends ModuleId = ModuleId> = {
  id: K;
  label: string;
  description: string;
  defaultNavLabel?: string;
  Public: PublicModule<K>;
};

export const moduleRegistry: { [K in ModuleId]: RegistryEntry<K> } = {
  hero: {
    id: "hero",
    label: "Hero / Banner",
    description: "Top-of-page title, date, banner image, and primary calls to action.",
    Public: HeroModule,
  },
  agenda: {
    id: "agenda",
    label: "Agenda",
    description: "Schedule of the day with optional highlights.",
    defaultNavLabel: "Schedule",
    Public: AgendaModule,
  },
  location: {
    id: "location",
    label: "Location",
    description:
      "OpenStreetMap embed of the venue with a 'Get directions' link. No API key required.",
    defaultNavLabel: "Where",
    Public: LocationModule,
  },
  rsvp: {
    id: "rsvp",
    label: "RSVP",
    description: "Family registrations with passcode-protected edits.",
    defaultNavLabel: "RSVP",
    Public: RsvpModule,
  },
  potluck: {
    id: "potluck",
    label: "Potluck",
    description: "Dish signups by category.",
    defaultNavLabel: "Potluck",
    Public: PotluckModule,
  },
  competition: {
    id: "competition",
    label: "Community competition",
    description:
      "Generic contest module: configurable for a cake bake-off, art show, recitation, etc.",
    defaultNavLabel: "Competition",
    Public: CompetitionModule,
  },
  junior: {
    id: "junior",
    label: "Junior program",
    description: "Parallel programming for children and teens.",
    defaultNavLabel: "Juniors",
    Public: JuniorModule,
  },
  donation: {
    id: "donation",
    label: "Donation campaign",
    description: "Per-event giving with suggested amounts and an optional fundraising goal.",
    defaultNavLabel: "Donate",
    Public: DonationModule,
  },
};

export const moduleOrder: ModuleId[] = [
  "hero",
  "agenda",
  "location",
  "rsvp",
  "potluck",
  "competition",
  "junior",
  "donation",
];

export function getEnabledModules(event: Event): Array<{
  id: ModuleId;
  config: ModuleConfigMap[ModuleId];
}> {
  const order = event.modulesOrder?.length ? event.modulesOrder : moduleOrder;
  const result: Array<{ id: ModuleId; config: ModuleConfigMap[ModuleId] }> = [];
  for (const id of order) {
    const config = event.modules[id];
    if (config?.enabled) {
      result.push({ id, config });
    }
  }
  return result;
}
